import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import style from '../../style/components/profile/AddEmailModal.module.scss';
import ErrorAlertContainer from '../ErrorAlertContainer';
import IndicatesRequiredField from '../IndicatesRequiredField';
import StyledModal from '../StyledModal';
import { isEmpty } from '../../utils/isEmpty';
import { addEmail, addEmailLegacy, fetchEmails } from '../../actions';
import { useDispatch, useSelector } from 'react-redux';
import InputWithError from '../InputWithError';
import { resetEmailState } from '../../actions/emails';
import validEmail from '../../utils/validEmail';
import EmailAddError from './EmailAddError';
import CheckboxGroup from '../CheckboxGroup';
import EmailVerificationModal from './EmailVerificationModal';

function validate(formData, emailTypes, emailList, t) {
  const errors = {};

  if (isEmpty(formData.address)) {
    errors.address = t('email.add.error_addressRequired');
  } else if (!validEmail(formData.address)) {
    errors.address = t('email.add.error_addressInvalid');
  } else if (emailList.find((email) => email.address === formData.address)) {
    errors.address = t('email.add.error_addressDuplicate');
  }

  // ensure at least one emailType is active
  if (!emailTypes.some((emailType) => emailType.active)) {
    errors.types = t('email.form.error_typeRequired');
  }

  return errors;
}

function initialFormData() {
  return {
    address: '',
  };
}

function initialEmailTypes(t) {
  return [
    { value: 'personal', label: t('email.form.label_personal'), active: false },
    { value: 'work', label: t('email.form.label_work'), active: false },
  ];
}

const AddEmailModal = ({ closeModal, isOpen }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState(initialFormData());
  const [emailTypes, setEmailTypes] = useState(initialEmailTypes(t));
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [hasInitiatedRequest, setHasInitiatedRequest] = useState(false);

  const toggleEmailType = (type) => {
    setEmailTypes(
      emailTypes.map((emailType) => {
        if (emailType.value === type) {
          return { ...emailType, active: !emailType.active };
        }
        return emailType;
      }),
    );
  };

  const emails = useSelector((state) => state.emails);
  const useOtpFlow = useSelector(
    (state) => state.featureFlags.axp_779_verifications_emails_api,
  );

  const handleChange = (event) => {
    const value =
      event.target.type === 'checkbox'
        ? event.target.checked
        : event.target.value;
    setFormData({ ...formData, [event.target.name]: value });
  };

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      const newErrors = validate(formData, emailTypes, emails.emails, t);
      setErrors(newErrors);

      if (!Object.keys(newErrors).length) {
        const resultTypes = emailTypes
          .filter((emailType) => emailType.active)
          .map((emailType) => emailType.value);

        if (useOtpFlow) {
          setHasInitiatedRequest(true);
          dispatch(addEmail(formData.address, resultTypes));
        } else {
          dispatch(addEmailLegacy(formData.address, resultTypes));
        }
      }
    },
    [dispatch, emails, formData, emailTypes, setErrors, useOtpFlow, t],
  );

  useEffect(() => {
    if (useOtpFlow) {
      // OTP flow: open verification modal only if this AddEmailModal initiated the request
      if (hasInitiatedRequest && emails.isSuccess && emails.inspectionId) {
        setShowVerificationModal(true);
        setHasInitiatedRequest(false); // Reset flag after opening modal
      }
    } else {
      // Legacy flow: close modal on success
      if (emails.isSuccess) {
        dispatch(resetEmailState());
        closeModal();
      }
    }
  }, [emails, useOtpFlow, dispatch, hasInitiatedRequest, closeModal]);

  const submitting = emails.isFetching;

  const handleVerificationSuccess = async () => {
    try {
      // Refresh email list
      await dispatch(fetchEmails());

      // Success flow - close modals and reset.
      // IMPORTANT: Call closeModal() BEFORE setShowVerificationModal(false).
      // After the await above, React 17 does NOT batch state updates, so each
      // setter triggers its own render. If we set showVerificationModal=false
      // first, there's a render where isOpen is still true and the AddEmailModal
      // form briefly flashes. By closing the parent modal first, isOpen becomes
      // false and the AddEmailModal stays hidden throughout.
      dispatch(resetEmailState());
      closeModal();
      setShowVerificationModal(false);
      setFormData(initialFormData());
      setEmailTypes(initialEmailTypes(t));
      setErrors({});
      setHasInitiatedRequest(false);
    } catch (error) {
      // Email verified but list refresh failed - show error but keep modal open
      setErrors({
        general: t('email.add.error_verifiedButRefreshFailed'),
      });
      setShowVerificationModal(false);
    }
  };

  const handleVerificationModalClose = () => {
    // Clear isSuccess/inspectionId from Redux to prevent the useEffect from
    // re-opening the verification modal on the next form submission.
    dispatch(resetEmailState());
    setShowVerificationModal(false);
    // Keep add email modal open in case user wants to try again
  };

  const handleLockoutClose = () => {
    dispatch(fetchEmails());
    dispatch(resetEmailState());
    setShowVerificationModal(false);
    setFormData(initialFormData());
    setEmailTypes(initialEmailTypes(t));
    setErrors({});
    setHasInitiatedRequest(false);
    closeModal();
  };

  return (
    <>
      <EmailVerificationModal
        isOpen={showVerificationModal}
        closeModal={handleVerificationModalClose}
        inspectionId={emails.inspectionId || ''}
        emailAddress={formData.address}
        onVerifySuccess={handleVerificationSuccess}
        onLockoutClose={handleLockoutClose}
      />
      <StyledModal
        isOpen={isOpen && !showVerificationModal}
        closeModal={() => {
          dispatch(resetEmailState());
          setFormData(initialFormData());
          setEmailTypes(initialEmailTypes(t));
          setErrors({});
          setHasInitiatedRequest(false);
          closeModal();
        }}
        submitting={submitting}
        title={t('email.add.title')}
        primaryButton={{
          hidden: false,
          text: t('email.add.button_send'),
          submittingText: t('email.add.button_saving'),
        }}
        wrapper={(children) => (
          <form
            onSubmit={handleSubmit}
            noValidate={true}
            className={style.wrapper}
          >
            {children}
          </form>
        )}
      >
        <IndicatesRequiredField />
        <InputWithError
          name="address"
          placeholder={t('email.add.placeholder_address')}
          label={t('email.add.label_address')}
          value={formData.address}
          onChange={handleChange}
          error={errors['address']}
          type="email"
          required
        />
        <CheckboxGroup
          label={t('email.form.label_emailType')}
          name="types"
          options={emailTypes}
          toggleCheckbox={toggleEmailType}
          error={errors['types']}
          required
        />

        {emails.emailAddError && <EmailAddError style={style} />}
        <ErrorAlertContainer />
      </StyledModal>
    </>
  );
};

export default AddEmailModal;
